import { useEffect, useState } from "react";

import { CustomFormField } from "utilities/forms";

import { getSenderOnEmailTypeChange } from "containers/admin/clients/touchpoint/components/email-builder/email-builder-content-settings-tab/email-builder-content-settings-tab.helper";

import { IEmailType } from "containers/admin/clients/client/components/email-types/email-types.types";
import type { EmailAttributes } from "hooks/api/use-touchpoint-versions";
import { ISenderDetails } from "containers/admin/clients/client/components/email-types/components/email-types-senders-form/email-types-senders-form.types";

interface EmailSettingsFieldsType {
  emailTypeOptions: IEmailType[];
  currentTouchpointVersion: EmailAttributes;
  setCurrentSender: React.Dispatch<React.SetStateAction<ISenderDetails>>;
  isTemplate: boolean;
}

const TEMPLATE_HIDDEN_FIELDS = new Set(["subjectLine", "preheader"]);

const useEmailSettingsFields = ({
  emailTypeOptions,
  currentTouchpointVersion,
  setCurrentSender,
  isTemplate,
}: EmailSettingsFieldsType) => {
  const [contentSettingsFields, setContentSettingsFields] = useState<CustomFormField[]>([]);

  const getChosenEmailType = (cb: (option: IEmailType) => boolean) => {
    return emailTypeOptions.find(cb)!;
  };

  const chosenEmailType = currentTouchpointVersion.iterableEmailTypeId
    ? getChosenEmailType(
        (option: IEmailType) => option.id === currentTouchpointVersion.iterableEmailTypeId,
      )
    : getChosenEmailType((option: IEmailType) => option.defaultForClient);

  const sender = getSenderOnEmailTypeChange(chosenEmailType, currentTouchpointVersion);

  useEffect(() => {
    setCurrentSender(sender);

    const touchpointFields: CustomFormField[] = [
      {
        name: "iterableEmailTypeId",
        component: "select",
        label: "Email Type",
        defaultValue: chosenEmailType?.id,
        placeholder: !emailTypeOptions.length ? "There are no Email Types" : undefined,
        options: emailTypeOptions.map(({ externalName, id }) => ({
          name: externalName,
          value: id,
        })),
      },
      {
        name: "senderName",
        component: "select",
        label: "Sender name",
        defaultValue: sender?.id,
        options: [],
      },
      {
        name: "senderEmail",
        component: "input",
        label: "Sender email",
        defaultValue: sender?.senderEmail,
        disabled: true,
      },
      {
        name: "replyToEmail",
        component: "input",
        label: "Reply-to email",
        defaultValue: sender?.replyToEmail,
        disabled: true,
      },
      {
        name: "subjectLine",
        component: "input",
        label: "Subject line",
        validations: {
          isRequired: true,
          maxLength: 120,
          maxLengthValidationMessage: "Subject line must be less than 120 characters",
        },
      },
      {
        name: "preheader",
        component: "textarea",
        label: "Preheader",
        helperText:
          "Enter a line of summary text that will be visible after the subject line in a contact's inbox. The ideal length is 40 to 100 characters.",
        validations: { isRequired: true },
      },
      {
        name: "advancedHeadSnippet",
        component: "textarea",
        label: "Head Snippet",
        helperText: "Scripts are applied directly before the closing </head> tag",
      },
      {
        name: "advancedBodySnippet",
        component: "textarea",
        label: "Body Snippet",
        helperText: "Scripts are applied directly before the closing </body> tag",
      },
    ];

    setContentSettingsFields(
      isTemplate
        ? touchpointFields.filter((field) => !TEMPLATE_HIDDEN_FIELDS.has(field.name))
        : touchpointFields,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailTypeOptions, chosenEmailType, isTemplate]);

  return contentSettingsFields;
};

export default useEmailSettingsFields;
